import { DescriptionField, DescriptionList } from "@dash/description-list";
import { Dialog } from "@dash/dialog";
import { Button, PostHogUserButton } from "@dash/form";
import { Header4, HeaderBar, Page } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { Tile } from "@dash/tile";
import { useToast } from "@dash/toast";
import { useTheme } from "@emotion/react";
import { useState } from "react";
import { MdCardGiftcard, MdShoppingCart } from "react-icons/md";
import { Link, useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

import { CartStatusChips } from "./CartStatusChips";

const environmentTemplate = process.env["NX_PUBLIC_ENVIRONMENT_TEMPLATE"];

const isProduction = () => {
  if (environmentTemplate === "production") return true;
  if (environmentTemplate === "development") return false;
  
  const localConfig = localStorage.getItem("config");
  if (localConfig) {
    try {
      const config = JSON.parse(localConfig);
      return config.urlTemplate === "PROD";
    } catch {
      return process.env["NODE_ENV"] === "production";
    }
  }
  
  return process.env["NODE_ENV"] === "production";
};

export const CartPage = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const fulfillmentClient = useClientManager("fulfillment");
  const theme = useTheme();
  const toast = useToast();

  const cartQuery = fulfillmentClient.getCartById().useQuery({ cartId: Uuid(cartId!) });

  const cancelRewardMutation = fulfillmentClient.cancelReward().useMutation();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [rewardIdToCancel, setRewardIdToCancel] = useState<string | null>(null);

  const getDisplayName = (productName?: string, variantName?: string) => {
    if (!productName) return "Ładowanie...";
    if (variantName) return `${productName} - ${variantName}`;
    return productName;
  };

  const getShopifyStoreName = () => {
    return isProduction() ? "megaron-eu" : "megaron-sandbox";
  };

  const extractShopifyId = (idString: string) => {
    const match = idString.match(/\/(\d+)$/);
    return match ? match[1] : idString;
  };

  const getShopifyProductUrl = (productId: string) => {
    const storeName = getShopifyStoreName();
    const numericId = extractShopifyId(productId);
    return `https://admin.shopify.com/store/${storeName}/products/${numericId}`;
  };

  const getShopifyDraftOrderUrl = (draftOrderId: string) => {
    const storeName = getShopifyStoreName();
    const numericId = extractShopifyId(draftOrderId);
    return `https://admin.shopify.com/store/${storeName}/draft_orders/${numericId}`;
  };

  const handleCancelReward = (rewardId: string) => {
    setRewardIdToCancel(rewardId);
    setShowCancelDialog(true);
  };

  const confirmCancelReward = async () => {
    if (!rewardIdToCancel) return;

    try {
      await cancelRewardMutation.mutateAsync({
        cartId: Uuid(cartId!),
        rewardId: Uuid(rewardIdToCancel),
      });

      await cartQuery.refetch();
      toast.info("Anulowano nagrodę");
      setShowCancelDialog(false);
      setRewardIdToCancel(null);
    } catch (error: any) {
      if (error?.error === "CartNotFound") {
        toast.error("Koszyk nie został znaleziony");
        await cartQuery.refetch();
      } else if (error?.error === "PurchaseNotFound") {
        toast.error("Zakup nie został znaleziony");
      } else {
        toast.error("Nie udało się anulować nagrody");
      }
      setShowCancelDialog(false);
      setRewardIdToCancel(null);
    }
  };

  return (
    <>
      <QuerySkeleton query={cartQuery} height="58px" width="100%">
        {(cart) => (
          <>
            <HeaderBar header={cart.id} />

            <Page css={{ display: "flex", flexDirection: "column", gap: "2rem", marginTop: "83px" }}>
              <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <Header4>Informacje podstawowe</Header4>
                <DescriptionList>
                  <DescriptionField label="Cookie ID">{cart.cookieId}</DescriptionField>
                <DescriptionField label="ID użytkownika">
                  {cart.userId ? (
                    <Link to={`/crm/customers/id/${cart.userId}`} css={{ textDecoration: "underline" }}>
                      {cart.userId}
                    </Link>
                  ) : (
                    "Anonimowy"
                  )}
                </DescriptionField>
                <DescriptionField label="Order Draft ID">
                  {cart.orderDraftId ? (
                    <a
                      href={getShopifyDraftOrderUrl(cart.orderDraftId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      css={{ textDecoration: "underline" }}
                    >
                      {cart.orderDraftId}
                    </a>
                  ) : (
                    "Brak"
                  )}
                </DescriptionField>
                <DescriptionField label="Status">
                  <CartStatusChips isArchived={cart.isArchived} isFulfilled={cart.isFulfilled} />
                </DescriptionField>
                <DescriptionField label="Data utworzenia">
                  {cart.createdAt.toLocaleString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </DescriptionField>
                <DescriptionField label="Ostatnia aktualizacja">
                  {cart.updatedAt.toLocaleString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </DescriptionField>
              </DescriptionList>
              {cart.userId && (
                <div css={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <PostHogUserButton userId={cart.userId} />
                </div>
              )}
            </div>

            <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Header4>Produkty ({cart.items.length})</Header4>
              {cart.items.length === 0 ? (
                <div css={{ padding: "1rem", textAlign: "center", color: theme.colors.secondaryText }}>
                  Brak produktów w koszyku
                </div>
              ) : (
                <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {cart.items.map((item) => (
                    <Tile
                      key={item.id}
                      stripe={
                        <div
                          css={{
                            color: theme.colors.primary,
                            opacity: 0.1,
                            padding: "0.5rem 0.875rem",
                            minHeight: 0,
                            alignSelf: "stretch",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <MdShoppingCart size={24} />
                        </div>
                      }
                    >
                      <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        <a
                          href={getShopifyProductUrl(item.productId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          css={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            textDecoration: "underline",
                            color: "inherit",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                        >
                          {getDisplayName(item.productName, item.variantName)}
                        </a>
                        <div css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>
                          Variant ID: {item.variantId}
                        </div>
                        <div css={{ fontSize: "0.75rem", fontWeight: 500, marginTop: "0.25rem" }}>
                          Ilość: {item.quantity}
                        </div>
                      </div>
                    </Tile>
                  ))}
                </div>
              )}
            </div>

            <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <Header4>Nagrody ({cart.rewards.length})</Header4>
              {cart.rewards.length === 0 ? (
                <div css={{ padding: "1rem", textAlign: "center", color: theme.colors.secondaryText }}>
                  Brak nagród w koszyku
                </div>
              ) : (
                <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {cart.rewards.map((reward) => (
                    <Tile
                      key={reward.id}
                      stripe={
                        <div
                          css={{
                            color: theme.colors.primary,
                            background: theme.colors.primary,
                            opacity: 1,
                            padding: "0.5rem 0.875rem",
                            minHeight: 0,
                            alignSelf: "stretch",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <MdCardGiftcard size={24} color="white" />
                        </div>
                      }
                      >
                      <div css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        <div
                          css={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "inherit",
                          }}
                        >
                          <a
                            href={getShopifyProductUrl(reward.productId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            css={{
                              textDecoration: "underline",
                              color: "inherit",
                              "&:hover": {
                                opacity: 0.8,
                              },
                            }}
                          >
                            {getDisplayName(reward.productName, reward.variantName)}
                          </a>
                        </div>
                        <div css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>
                          Variant ID: {reward.variantId}
                        </div>
                        {reward.purchaseId && (
                          <div css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>
                            Purchase ID: {reward.purchaseId}
                          </div>
                        )}
                        <div css={{ fontSize: "0.75rem", fontWeight: 500, marginTop: "0.25rem" }}>
                          Cena: {reward.pointsPrice.toString()} punktów
                        </div>
                        {reward.engraving && (
                          <div css={{ fontSize: "0.75rem", fontStyle: "italic", color: theme.colors.secondaryText }}>
                            Grawer: "{reward.engraving}"
                          </div>
                        )}
                        {cart.userId && (
                          <div css={{ marginTop: "0.5rem" }}>
                            <Button
                              variant="outline"
                              color="danger"
                              size="small"
                              onClick={() => handleCancelReward(reward.id)}
                              isDisabled={cancelRewardMutation.isLoading}
                            >
                              Anuluj nagrodę
                            </Button>
                          </div>
                        )}
                      </div>
                    </Tile>
                  ))}
                </div>
              )}
            </div>
          </Page>
          </>
        )}
      </QuerySkeleton>
      {showCancelDialog && (
        <Dialog
          header="Potwierdź anulowanie"
          onClose={() => setShowCancelDialog(false)}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(false)}
                isDisabled={cancelRewardMutation.isLoading}
                css={{ flex: 1 }}
              >
                Anuluj
              </Button>
              <Button
                variant="primary"
                color="danger"
                onClick={confirmCancelReward}
                isDisabled={cancelRewardMutation.isLoading}
                css={{ flex: 1, marginLeft: "0.5rem" }}
              >
                Tak, anuluj nagrodę
              </Button>
            </>
          }
        >
          <div css={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
            Czy na pewno chcesz anulować tę nagrodę?
          </div>
        </Dialog>
      )}
    </>
  );
};
